import { useEffect, useState } from 'react';
import { EJobStatus } from 'business/models/CustomModels';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import { JobTypeDTO } from 'components/main/Setup/Imaging/ImageManagementTask/constants';
import connectionHubManager from 'connectionHub/connectionHubManager';
import RMHub from 'connectionHub/RMHub';
import { useRMDispatch } from 'redux/hooks';
import { upsertBackgroundJob } from 'redux/reducers/backgroundJobsReducer';
import { normalizeJobDataObj } from 'redux/utils/archivingProgressUtils';
import {
  ARCHIVING_PAUSED_BY_USER_STATUS,
  ARCHIVING_STARTING_OVER_STATUS,
  ARCHIVING_WAITING_FOR_STORAGE_STATUS,
} from '../constants';

export {
  getArchivedExperimentIds,
  normalizeJobDataObj,
} from 'redux/utils/archivingProgressUtils';

const PROGRESS_HUB_NAME = 'progresshub';
const PROGRESS_HUB_PATH = '/progressHub';

const isJobRunning = (job: BackgroundJobDTO) => job.jobStatus === EJobStatus.InProgress;

const isJobQueued = (job: BackgroundJobDTO) => {
  if (job.jobStatus !== EJobStatus.BeforeStart) {
    return false;
  }
  const progressData = normalizeJobDataObj(job.dataObj) ?? {};
  const status = progressData.status ?? progressData.Status;
  return status === 'Pending';
};

export const isArchivingJobWaitingForStorage = (job: BackgroundJobDTO | null) => {
  if (!job) {
    return false;
  }

  const progressData = normalizeJobDataObj(job.dataObj) ?? {};
  const status = progressData.status ?? progressData.Status;
  return status === ARCHIVING_WAITING_FOR_STORAGE_STATUS;
};

export const isArchivingJobPaused = (job: BackgroundJobDTO | null) => {
  if (!job) {
    return false;
  }

  const progressData = normalizeJobDataObj(job.dataObj) ?? {};
  const status = progressData.status ?? progressData.Status;

  return status === ARCHIVING_PAUSED_BY_USER_STATUS;
};

export const isArchivingJobStartingOver = (job: BackgroundJobDTO | null) => {
  if (!job) {
    return false;
  }

  const progressData = normalizeJobDataObj(job.dataObj) ?? {};
  const status = progressData.status ?? progressData.Status;

  return status === ARCHIVING_STARTING_OVER_STATUS;
};

export const useArchivingProgressHub = (
  jobTypes: JobTypeDTO[] = [JobTypeDTO.Archiving],
) => {
  const dispatch = useRMDispatch();
  const [connection, setConnection] = useState<RMHub | null>(null);
  const jobTypesKey = jobTypes.join(',');

  useEffect(() => {
    let cancelled = false;

    const initConnection = async () => {
      try {
        const conn = await connectionHubManager.getConnection(
          PROGRESS_HUB_NAME,
          PROGRESS_HUB_PATH,
        );
        if (!cancelled) {
          setConnection(conn);
        }
      } catch (error) {
        console.error('Failed to initialize progress hub connection:', error);
      }
    };

    void initConnection();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!connection) {
      return;
    }

    const watchedJobTypes = jobTypesKey ? jobTypesKey.split(',').map(Number) : [];
    const matchesWatchedJobType = (job: BackgroundJobDTO) =>
      watchedJobTypes.includes(Number(job?.jobType));

    const handleJobUpdate = (job: BackgroundJobDTO) => {
      if (!job || !matchesWatchedJobType(job)) {
        return;
      }

      dispatch(upsertBackgroundJob(job));
    };

    const handleReceiveJobsByType = (jobs: BackgroundJobDTO[]) => {
      const matchingJobs = jobs?.filter(matchesWatchedJobType) ?? [];
      matchingJobs.forEach(handleJobUpdate);
    };

    const requestWatchedJobs = () => {
      watchedJobTypes.forEach((jobType) => {
        connection.invoke('RequestJobsByType', jobType).catch((error) => {
          console.error(
            `Failed invoke 'RequestJobsByType' for job type ${jobType}:`,
            error,
          );
        });
      });
    };

    connection.on('AddNewJob', handleJobUpdate);
    connection.on('UpdateJobProgress', handleJobUpdate);
    connection.on('ReceiveJobsByType', handleReceiveJobsByType);
    requestWatchedJobs();

    const reconnectedSubscription = connection.reconnected.add(requestWatchedJobs);

    return () => {
      connection.off('AddNewJob', handleJobUpdate);
      connection.off('UpdateJobProgress', handleJobUpdate);
      connection.off('ReceiveJobsByType', handleReceiveJobsByType);
      reconnectedSubscription.dispose();
    };
  }, [connection, dispatch, jobTypesKey]);
};

export const isArchivingJobActive = (job: BackgroundJobDTO | null) =>
  job != null &&
  (isJobRunning(job) ||
    isJobQueued(job) ||
    isArchivingJobWaitingForStorage(job) ||
    isArchivingJobPaused(job) ||
    isArchivingJobStartingOver(job));

export const isUnarchivingJobActive = (job: BackgroundJobDTO | null) =>
  job != null && (isJobRunning(job) || isJobQueued(job));

export const getArchivingStatusLabel = (job: BackgroundJobDTO | null) => {
  if (!job) {
    return '';
  }

  const progressData = normalizeJobDataObj(job.dataObj) ?? {};
  const errorMessage = progressData.errorMessage ?? progressData.ErrorMessage;
  const status = progressData.status ?? progressData.Status;

  if (typeof errorMessage === 'string' && errorMessage.length > 0) {
    return errorMessage;
  }

  if (typeof status === 'string' && status.length > 0) {
    return job.name ? `${status} (${job.name})` : status;
  }

  return job.name ?? '';
};

export const getArchivingArchivedLabel = (job: BackgroundJobDTO | null) => {
  const progressData = normalizeJobDataObj(job?.dataObj) ?? {};
  const totalExperiments = Number(
    progressData.totalExperiments ?? progressData.TotalExperiments ?? 0,
  );
  const processedExperiments = Number(
    progressData.processedExperiments ?? progressData.ProcessedExperiments ?? 0,
  );

  if (totalExperiments <= 0) {
    return '';
  }

  return `${processedExperiments}/${totalExperiments}`;
};

// Single archived count shown on a completed job (e.g. "Experiment has been
// archived (78)"), as opposed to the in-progress "processed/total" fraction.
// Reflects the number of experiments successfully archived (processed), so a
// partial failure shows the count that actually made it into the archive.
export const getArchivingArchivedCount = (job: BackgroundJobDTO | null) => {
  const progressData = normalizeJobDataObj(job?.dataObj) ?? {};
  const totalExperiments = Number(
    progressData.totalExperiments ?? progressData.TotalExperiments ?? 0,
  );
  const processedExperiments = Number(
    progressData.processedExperiments ?? progressData.ProcessedExperiments ?? 0,
  );

  if (totalExperiments <= 0) {
    return '';
  }

  return `${processedExperiments}`;
};
